"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { invalidateAfterVoiceAgentChange, useActiveVoiceTrialQuery } from "@/hooks/queries/voice-trial";
import type { ActiveVoiceTrialView } from "@/server-actions/voice-trial";
import { useTranslations } from "next-intl"


interface DangerZoneSettingsProps {
  agentId: string;
  agentName: string;
  onDelete?: () => void;
}

export function DangerZoneSettings({ agentId, agentName, onDelete }: DangerZoneSettingsProps) {
  const tUi = useTranslations("Voice.AgentsVoiceSettingsDangerZoneSettings")

  const router = useRouter();
  const { fetchWithAuth } = useAuth();
  const queryClient = useQueryClient();
  const notice = trialNotice(useActiveVoiceTrialQuery(), agentId);

  const [confirmText, setConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (confirmText !== agentName) {
      setError(tUi("pleaseTypeTheAgentNameToConfirmDeletion"));
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`/api/agents/voice/${agentId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        void invalidateAfterVoiceAgentChange(queryClient);
        onDelete?.();
        router.push("?page=voice-agents");
      } else {
        const data = await response.json();
        setError(data.error || "Failed to delete agent");
      }
    } catch (err) {
      console.error("Error deleting agent:", err);
      setError(tUi("failedToDeleteAgent"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            <span><span key={tUi("dangerZone")}>{tUi("dangerZone")}</span></span>
          </CardTitle>
          <CardDescription>
            <span key={tUi("irreversibleAndDestructiveActions")}>{tUi("irreversibleAndDestructiveActions")}</span></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-medium text-red-800 mb-2"><span key={tUi("deleteThisAgent")}>{tUi("deleteThisAgent")}</span></h4>
            <p className="text-sm text-red-700 mb-4">
              <span key={tUi("onceYouDeleteAnAgentThereIsNo")}>{tUi("onceYouDeleteAnAgentThereIsNo")}</span></p>
            {notice === "checking" && (
              <p className="text-sm text-red-700 mb-4"><span key={tUi("checkingWhetherThisAgentRunsYourVoiceTrial")}>{tUi("checkingWhetherThisAgentRunsYourVoiceTrial")}</span></p>
            )}
            {notice === "ends_trial" && (
              <p className="text-sm font-medium text-red-800 mb-4">
                <span key={tUi("thisAgentRunsYourVoiceTrialDeletingIt")}>{tUi("thisAgentRunsYourVoiceTrialDeletingIt")}</span></p>
            )}
            {notice === "unknown" && (
              <p className="text-sm font-medium text-red-800 mb-4">
                <span key={tUi("weCouldNotCheckYourVoiceTrialIf")}>{tUi("weCouldNotCheckYourVoiceTrialIf")}</span></p>
            )}

            <div className="space-y-3">
              <div>
                <Label htmlFor="confirm-delete" className="text-red-700">
                  <span><span key={tUi("type")}>{tUi("type")}</span></span> <span className="font-mono font-bold">{agentName}</span> <span><span key={tUi("toConfirm")}>{tUi("toConfirm")}</span></span>
                </Label>
                <Input
                  id="confirm-delete"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={tUi("typeAgentNameHere")}
                  className="mt-1"
                />
              </div>

              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting || notice === "checking" || confirmText !== agentName}
                className="w-full"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span><span key={tUi("deleting")}>{tUi("deleting")}</span></span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    <span><span key={tUi("deleteAgent")}>{tUi("deleteAgent")}</span></span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

type TrialNotice = "checking" | "ends_trial" | "unknown" | "none";

/** Deleting the trial's agent can use the one trial up, so an unknown trial state is never read as "no trial". */
function trialNotice(query: UseQueryResult<ActiveVoiceTrialView>, agentId: string): TrialNotice {
  switch (query.status) {
    case "pending":
      // A disabled query never settles; waiting on it would block the delete for good.
      return query.fetchStatus === "fetching" ? "checking" : "unknown";
    case "error":
      return "unknown";
    case "success":
      return query.data.voiceAgentId === agentId && query.data.hasUsage ? "ends_trial" : "none";
  }
}
